import { useState, useMemo } from 'react'
import styled from 'styled-components'
import { ConfigProvider, Select } from 'antd'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useTranslation } from "react-i18next"

import SelectCourse from './pages/SelectCourse'
import SelectCourseSpecialCourse from './pages/SelectCourseSpecialCourse'
import SelectCourseDate from './pages/SelectCourseDate'
import SelectCourseReservation from './pages/SelectCourseReservation'
import SelectCourseTerms from './pages/SelectCourseTerms'
import SelectCourseCheckout from './pages/SelectCourseCheckout'

import ListStep from './components/ListStep'
import CircleStep from './components/CircleStep'

import { StepViewEnum, WpEnvCityEnum, FeaturesGateEnum, WpEnvCityNameEnum } from './enums'
import { SelectCourseStepsType, CourseDataType, CourseDataQuantityType, EventType, CustomTicketType, AmeliaTicketType, FormReservationType, PayloadAmeliaPaymentWcType, OrderSummaryDataType, FormTaxInvoiceType, ToggleFeaturesType, CourseCategoryType, SoftBookingType, PrivateCourseType, PrivateRoomType, GenderOptionType } from './types'

import axios from 'axios'
import useSWR, { Fetcher } from 'swr'
import { htmlDecode } from './helpers'
import {
  pushDataLayerAddToCart,
  pushDataLayerDatetimeSelect,
  pushDataLayerCustomerDetailEntered,
  pushDataLayerBeginCheckout
} from './helpers/gtm'
import { bookingFeatures } from './configs/features'

import { coursesData as mockCoursesData, orderSummaryData as mockOrderSummaryData } from './mockData'

declare global {
  interface Window {
    coursesData: CourseDataType[]
    courseCategories: CourseCategoryType[]
    orderSummaryData: OrderSummaryDataType
    wpAjaxUrl: string
    yuzuBaseURL: string
    ajaxNonce: string
    ajaxNonceCreateOrder: string
    ajaxNonceEditBooking: string
    ajaxNonceUpdateTaxInvoiceOrder: string
    isAdmin: boolean
    isAdminMode: boolean
    isEditBookingMode: boolean
    orderId: string
    bookingId: string
    eventId: string
    initialSelectedCourses: CourseDataQuantityType[]
    currentAmeliaBookingData: any
    jQuery: any
    isDiscount: boolean
    initialTaxInvoiceForm: FormTaxInvoiceType
    currencySymbol: string
    WP_ENV_CITY: keyof typeof WpEnvCityEnum | ''
    WP_ENV_CITY_NAME: WpEnvCityNameEnum | ''
    featuresGate: Record<FeaturesGateEnum, boolean>
    listSoftBooking: SoftBookingType[]
    listPrivateCourse: PrivateCourseType[]
    listPrivateRoom: PrivateRoomType[]
    genderOptions: GenderOptionType[]
    user_displayname: string
    user_email: string
  }
}

interface ResRequestEvents {
  events: EventType[]
}

window.yuzuBaseURL = window.yuzuBaseURL || 'http://127.0.0.1:8888/wp-test-yuzu-theme'
window.wpAjaxUrl = window.wpAjaxUrl || 'http://127.0.0.1:8888/wp-test-yuzu-theme/wp-admin/admin-ajax.php'
const windowAjaxNonce = window.ajaxNonce || ''
const windowAjaxNonceCreateOrder = window.ajaxNonceCreateOrder || ''
const windowAjaxNonceEditBooking = window.ajaxNonceEditBooking || ''
const windowAjaxNonceUpdateTaxInvoiceOrder = window.ajaxNonceUpdateTaxInvoiceOrder || ''
const windowCoursesData = window.coursesData || [] // mockCoursesData | []
const windowCourseCategories = window.courseCategories || []
const windowOrderSummaryData = window.orderSummaryData || null // mockOrderSummaryData | null
const windowIsAdmin = window.isAdmin ?? false
const windowIsAdminMode = window.isAdminMode ?? false
const windowIsEditBookingMode = window.isEditBookingMode ?? false
const windowOrderId = window.orderId ?? ''
const windowBookingId = window.bookingId ?? ''
const windowEventId = window.eventId ?? ''
const windowInitialSelectedCourses = window.initialSelectedCourses ?? []
const windowCurrentAmeliaBookingData = window.currentAmeliaBookingData ?? null
const windowIsDiscount = window.isDiscount ?? false
const windowInitialTaxInvoiceForm = window.initialTaxInvoiceForm ?? null
const windowCurrencySymbol = window.currencySymbol ?? ''
const windowWP_ENV_CITY = window.WP_ENV_CITY ?? ''
const windowWP_ENV_CITY_NAME = window.WP_ENV_CITY_NAME ?? ''
const windowListSoftBooking = window.listSoftBooking
const windowListPrivateCourse = window.listPrivateCourse
const windowListPrivateRoom = window.listPrivateRoom
const windowGenderOptions = window.genderOptions ?? []
const featuresGate = window.featuresGate

const antdTheme = {
  token: {
    colorPrimary: '#B7A483',
  },
}

const initailStep = (() => {
  if (windowIsEditBookingMode) {
    return StepViewEnum.date
  } else {
    return windowOrderSummaryData ? StepViewEnum.checkout : StepViewEnum.selectCourse;
  }
})()

const fetcher: Fetcher<ResRequestEvents, string> = (url: string) => axios.get(url).then(res => res.data.data)
// // #Filter events only in period 4 months
// const urlRequestEventsDataA = `${window.wpAjaxUrl}?action=wpamelia_api&call=/events&dates=${dayjs().format('YYYY-MM-DD')},${dayjs().add(1, 'month').format('YYYY-MM-DD')}&page=0&recurring=0`
// const urlRequestEventsDataB = `${window.wpAjaxUrl}?action=wpamelia_api&call=/events&dates=${dayjs().add(1, 'month').add(1, 'day').format('YYYY-MM-DD')},${dayjs().add(2, 'month').format('YYYY-MM-DD')}&page=0&recurring=0`
// const urlRequestEventsDataC = `${window.wpAjaxUrl}?action=wpamelia_api&call=/events&dates=${dayjs().add(2, 'month').add(1, 'day').format('YYYY-MM-DD')},${dayjs().add(3, 'month').format('YYYY-MM-DD')}&page=0&recurring=0`
// const urlRequestEventsDataD = `${window.wpAjaxUrl}?action=wpamelia_api&call=/events&dates=${dayjs().add(3, 'month').add(1, 'day').format('YYYY-MM-DD')},${dayjs().add(4, 'month').format('YYYY-MM-DD')}&page=0&recurring=0`

const urlRequestEventsDataFromAPICustomCache = `${window.wpAjaxUrl}?action=list_amelia_booking`
const urlRequestAmeliaCreateCart = `${window.wpAjaxUrl}?action=wpamelia_api&call=/payment/wc`

function App() {
  const { t } = useTranslation()
  const [step, setStep] = useState<StepViewEnum>(initailStep)
  const [selectedCourses, setSelectedCourses] = useState<CourseDataQuantityType[]>(windowInitialSelectedCourses)
  const [selectedEvent, setSelectedEvent] = useState<EventType>()
  const [selectCalendarDate, setSelectCalendarDate] = useState<Dayjs>()
  const [reservationFormData, setReservationFormData] = useState<FormReservationType>()
  const [phoneCountry, setPhoneCountry] = useState<string>(windowWP_ENV_CITY)
  const [isRequestingCreateOrder, setIsRequestingCreateOrder] = useState<boolean>(false)
  const [isRequestingChangeBooking, setIsRequestingChangeBooking] = useState<boolean>(false)
  const [isRequestingUpdateTaxInvoiceOrder, setIsRequestingUpdateTaxInvoiceOrder] = useState<boolean>(false)
  // const [indexLoadFetch, setIndexLoadFetch] = useState<number>(0)
  const [eventsData, setEventsData] = useState<EventType[]>([])

  const mergeFetchEventData = (index: number, data: ResRequestEvents) => {
    const _eventsData = data?.events ? data?.events : []
    // setIndexLoadFetch(index)
    setEventsData([...eventsData, ..._eventsData])
  }

  // useSWR<ResRequestEvents>(indexLoadFetch === 0 ? urlRequestEventsDataA: null, fetcher, { onSuccess: (data) => mergeFetchEventData(1, data)})
  // useSWR<ResRequestEvents>(indexLoadFetch === 1 ? urlRequestEventsDataB : null, fetcher, { onSuccess: (data) => mergeFetchEventData(2, data)})
  // useSWR<ResRequestEvents>(indexLoadFetch === 2 ? urlRequestEventsDataC : null, fetcher, { onSuccess: (data) => mergeFetchEventData(3, data)})
  // useSWR<ResRequestEvents>(indexLoadFetch === 3 ? urlRequestEventsDataD : null, fetcher, { onSuccess: (data) => mergeFetchEventData(4, data)})
  useSWR<ResRequestEvents>(urlRequestEventsDataFromAPICustomCache, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onSuccess: (data) => mergeFetchEventData(1, data)
  })

  const selectCourseSteps: SelectCourseStepsType[] = windowIsEditBookingMode ? [
    { slug: StepViewEnum.date, name: t('date_and_time') },
  ] : [
    { slug: StepViewEnum.selectCourse, name: t('select_courses') },
    { slug: StepViewEnum.date, name: t('date_and_time') },
    { slug: StepViewEnum.reservation, name: t('reservation_detail') },
    { slug: StepViewEnum.terms, name: t('terms_and_agreements') },
    { slug: StepViewEnum.checkout, name: t('checkout') },
  ]

  const currentStepIndex = useMemo(() => {
    const index = selectCourseSteps.findIndex(_step => _step.slug === step)
    return index ?? 0
  }, [step])

  let StepContent = null
  switch (step) {
    case StepViewEnum.selectCourse:
      StepContent = featuresGate.FEATURE_SPECIAL_COURSE ? (
        <SelectCourseSpecialCourse
          isAdmin={windowIsAdmin}
          isAdminMode={windowIsAdminMode}
          coursesData={windowCoursesData}
          courseCategories={windowCourseCategories}
          selectedCourses={selectedCourses}
          onClick13More={() => window.location.href = `${window.yuzuBaseURL}/contact-us`}
          onClickNext={(courseDataQuantity) => {
            pushDataLayerAddToCart(courseDataQuantity, windowCourseCategories, windowIsAdmin)
            setSelectedCourses(courseDataQuantity)
            setStep(StepViewEnum.date)
          }}
          onBack={() => window.location.href = `${window.yuzuBaseURL}/booking-schedule`}
          currencySymbol={windowCurrencySymbol}
          bookingFeatures={bookingFeatures}
          listPrivateCourse={windowListPrivateCourse}
          listPrivateRoom={windowListPrivateRoom}
        />
      ) : (
        <SelectCourse
          isAdmin={windowIsAdmin}
          isAdminMode={windowIsAdminMode}
          coursesData={windowCoursesData}
          selectedCourses={selectedCourses}
          onClick13More={() => window.location.href = `${window.yuzuBaseURL}/contact-us`}
          onClickNext={(courseDataQuantity) => {
            pushDataLayerAddToCart(courseDataQuantity, windowCourseCategories, windowIsAdmin)
            setSelectedCourses(courseDataQuantity)
            setStep(StepViewEnum.date)
          }}
          onBack={() => window.location.href = `${window.yuzuBaseURL}/booking-schedule`}
          currencySymbol={windowCurrencySymbol}
          bookingFeatures={bookingFeatures}
          listPrivateRoom={windowListPrivateRoom}
        />
      )
      break
    case StepViewEnum.date:
      StepContent = (
        <SelectCourseDate
          isAdmin={windowIsAdmin}
          isAdminMode={windowIsAdminMode}
          eventsData={eventsData}
          listSoftBooking={windowListSoftBooking}
          selectedEvent={selectedEvent}
          selectCalendarDate={selectCalendarDate}
          selectedCourses={selectedCourses}
          excludeEventIds={windowEventId ? [windowEventId] : []}
          isEditBookingMode={windowIsEditBookingMode}
          isRequestingChangeBooking={isRequestingChangeBooking}
          onClickChangeBooking={handleMoveBooking}
          onClickBack={() => {
            resetSelectCalendar()
            setStep(StepViewEnum.selectCourse)
          }}
          onClickBackPrivateRoomBooking={() => {
            setSelectedCourses([])
            resetSelectCalendar()
            setStep(StepViewEnum.selectCourse)
          }}
          onClickNext={(event) => {
            pushDataLayerDatetimeSelect(event)
            setSelectedEvent(event)
            setSelectCalendarDate(dayjs(event.periods[0].periodStart, 'YYYY-MM-DD HH:mm:ss'))
            setStep(StepViewEnum.reservation)
          }}
          wpEnvCity={windowWP_ENV_CITY}
          listPrivateCourse={windowListPrivateCourse}
        />
      )
      break
    case StepViewEnum.reservation:
      StepContent = (
        <SelectCourseReservation
          phoneCountry={phoneCountry}
          setPhoneCountry={setPhoneCountry}
          bookingFeatures={bookingFeatures}
          genderOptions={windowGenderOptions}
          reservationFormData={reservationFormData}
          onClickBack={() => setStep(StepViewEnum.date)}
          onClickNext={(formData) => {
            pushDataLayerCustomerDetailEntered(formData)
            setReservationFormData(formData)
            setStep(StepViewEnum.terms)
          }}
        />
      )
      break
    case StepViewEnum.terms:
      StepContent = (
        <SelectCourseTerms
          onClickBack={() => setStep(StepViewEnum.reservation)}
          isRequestingCreateOrder={isRequestingCreateOrder}
          onClickNext={handleCreateNewBookingOrder}
          wpEnvCity={windowWP_ENV_CITY}
        />
      )
      break
    case StepViewEnum.checkout:
      StepContent = windowOrderSummaryData ? (
        <SelectCourseCheckout
          taxInvoiceFormData={windowInitialTaxInvoiceForm}
          isAdmin={windowIsAdmin}
          isAdminMode={windowIsAdminMode}
          orderSummaryData={windowOrderSummaryData}
          isRequestingUpdateTaxInvoiceOrder={isRequestingUpdateTaxInvoiceOrder}
          onCheckout={async (values) => {
            pushDataLayerBeginCheckout(windowOrderSummaryData, windowCoursesData, windowCourseCategories)
            await handleUpdateTaxInvoiceBookingOrder(values)
          }}
          wpEnvCity={windowWP_ENV_CITY}
          listPrivateRoom={windowListPrivateRoom}
        />
      ) : null
      break
  }

  return (
    <ConfigProvider theme={antdTheme}>
      <Wrapper>
        <BoxLeft>
          <ListSteps>
            {selectCourseSteps.map((_step, i) => {
              return (
                <ListStep
                  key={i}
                  passed={i <= currentStepIndex}
                  active={i === currentStepIndex}
                  onClick={() => {
                    if (i <= currentStepIndex && step !== StepViewEnum.checkout) {
                      _step.slug === StepViewEnum.selectCourse && resetSelectCalendar()
                      setStep(_step.slug)
                    }
                  }}
                >
                  {_step.name}
                </ListStep>
              )
            })}
          </ListSteps>
        </BoxLeft>
        <BoxRight>
          {
            windowWP_ENV_CITY === 'vn' && (step === StepViewEnum.selectCourse || step === StepViewEnum.date || step === StepViewEnum.reservation) && (
              <SelectWrapper>
                {t('branch')}
                <Select
                  defaultValue={windowWP_ENV_CITY_NAME}
                  onChange={(val) => {
                    switch (val) {
                      case WpEnvCityNameEnum.vietname_hcm:
                        window.location.href = '/vn/booking'
                        break
                      case WpEnvCityNameEnum.vietname_hanoi:
                        window.location.href = '/vn/hn/booking'
                        break
                    }
                  }}>
                  <Select.Option value="HO CHI MINH"><OptionCountry><div className="react-tel-input"><div className="flag vn"></div></div>Ho Chi Minh</OptionCountry></Select.Option>
                  <Select.Option value="HANOI"><OptionCountry><div className="react-tel-input"><div className="flag vn"></div></div>Hanoi</OptionCountry></Select.Option>
                </Select>
              </SelectWrapper>
            )
          }
          <StepDetailsWrapper>
            <div>
              <StepName className="yz-h3" onClick={goToNextStep}>{selectCourseSteps[currentStepIndex].name}</StepName>
              {selectCourseSteps[currentStepIndex + 1] && (<NextStepDetail>{t('next')}: {selectCourseSteps[currentStepIndex + 1].name}</NextStepDetail>)}
            </div>
            <CircleStepWrapper>
              <CircleStep maxStep={selectCourseSteps.length} currentStep={currentStepIndex + 1} />
            </CircleStepWrapper>
          </StepDetailsWrapper>

          {StepContent}
        </BoxRight>
      </Wrapper>
    </ConfigProvider>
  )

  function resetSelectCalendar() {
    setSelectedEvent(undefined)
    setSelectCalendarDate(undefined)
  }

  function goToNextStep() {
    const nextStep = currentStepIndex < 4 ? currentStepIndex + 1 : 4
    setStep(selectCourseSteps[nextStep].slug)
  }

  async function handleUpdateTaxInvoiceBookingOrder(taxInvoice: FormTaxInvoiceType) {
    if (isRequestingUpdateTaxInvoiceOrder) {
      return false
    }

    setIsRequestingUpdateTaxInvoiceOrder(true)
    const formData = new FormData()
    formData.append('action', 'updateTaxInvoiceOrder')
    formData.append('order_id', windowOrderId)
    formData.append('company_name', taxInvoice.tax_invoice ? taxInvoice.company_name ? taxInvoice.company_name : '' : '')
    formData.append('address_1', taxInvoice.tax_invoice ? taxInvoice.address_1 ? taxInvoice.address_1 : '' : '')
    formData.append('address_2', taxInvoice.tax_invoice ? taxInvoice.address_2 ? taxInvoice.address_2 : '' : '')
    formData.append('tax_id', taxInvoice.tax_invoice ? taxInvoice.tax_id ? taxInvoice.tax_id : '' : '')
    formData.append('branch_number', taxInvoice.tax_invoice ? taxInvoice.branch_number ? taxInvoice.branch_number : '' : '')
    formData.append('branch_name', taxInvoice.tax_invoice ? taxInvoice.branch_name ? taxInvoice.branch_name : '' : '')
    formData.append('nonce', windowAjaxNonceUpdateTaxInvoiceOrder)

    try {
      await axios({
        method: 'POST',
        url: window.wpAjaxUrl,
        data: formData,
      })

      setIsRequestingUpdateTaxInvoiceOrder(false)
      window.location.href = windowOrderSummaryData.link_checkout_payment
    }
    catch (error) {
      setIsRequestingUpdateTaxInvoiceOrder(false)
      console.log('Error on update tax invoice order: ', error)
    }
  }

  async function handleCreateNewBookingOrder() {
    if (isRequestingCreateOrder) {
      return false
    }

    if (selectedEvent && reservationFormData) {
      setIsRequestingCreateOrder(true)
      const ameliaCreateCartPayload: PayloadAmeliaPaymentWcType = {
        type: 'event',
        eventId: selectedEvent?.id,
        timeZone: 'Asia/Bangkok',
        returnUrl: window.yuzuBaseURL,
        payment: {
          amount: '15000.00',
          gateway: 'wc',
          currency: 'THB'
        },
        bookings: [{
          customer: {
            email: reservationFormData.email,
            firstName: reservationFormData.firstname,
            lastName: reservationFormData.lastname,
            phone: reservationFormData.phone,
            countryPhoneIso: phoneCountry
          },
          ticketsData: selectedEvent.customTickets.map(ticket => {
            const selectedCourseTarget = selectedCourses.find(selectedCourse => htmlDecode(selectedCourse.name).toLowerCase() === ticket.name.toLowerCase())
            const persons = selectedCourseTarget ? selectedCourseTarget.quantity : 0
            return {
              eventTicketId: ticket.id,
              persons,
              name: ticket.name,
              price: ticket.price
            }
          }),
          deposit: true,
          customFields: {},
          customerId: 0,
          extras: [],
          persons: 1
        }]
      }

      const formData = new FormData()
      formData.append('action', 'createOrderByAmeliaCart')
      formData.append('firstname', reservationFormData.firstname ? reservationFormData.firstname : '')
      formData.append('lastname', reservationFormData.lastname ? reservationFormData.lastname : '')
      formData.append('email', reservationFormData.email ? reservationFormData.email : '')
      formData.append('phone', reservationFormData.phone ? reservationFormData.phone : '')
      formData.append('gender', reservationFormData.gender ? reservationFormData.gender : '')
      formData.append('company_name', '')
      formData.append('address_1', '')
      formData.append('address_2', '')
      formData.append('additional_request_details', reservationFormData.additional_request_details ? reservationFormData.additional_request_details : '')
      formData.append('special_occasion_details', reservationFormData.special_occasion_details ? reservationFormData.special_occasion_details : '')
      formData.append('dietary_restriction_details', reservationFormData.dietary_restriction_details ? reservationFormData.dietary_restriction_details : '')
      formData.append('is_discount', windowIsDiscount ? '1' : '')
      formData.append('nonce', windowAjaxNonceCreateOrder)

      // If select course is private room
      if (selectedCourses[0].number_of_party) {
        formData.append('number_of_party', `${selectedCourses[0].number_of_party}`)
      }

      try {
        await axios.post(urlRequestAmeliaCreateCart, ameliaCreateCartPayload)
        const resCreateOrder = await axios({
          method: 'POST',
          url: window.wpAjaxUrl,
          data: formData,
        })

        if (resCreateOrder?.data?.success) {
          const orderNumber = resCreateOrder?.data?.data?.order_number
          // Orders created from 2026-10-15 need their key in the booking-summary link.
          const orderKey = resCreateOrder?.data?.data?.order_key
          const keyParam = orderKey ? `&key=${encodeURIComponent(orderKey)}` : ''
          let urlAdminMode = windowIsAdminMode ? '&mode=admin' : ''
          urlAdminMode = windowIsDiscount ? '&discount=1' : ''
          window.location.href = `${window.location.origin}${window.location.pathname}?order_number=${orderNumber}${keyParam}${urlAdminMode}`
        } else {
          setIsRequestingCreateOrder(false)
        }
      }
      catch (error) {
        setIsRequestingCreateOrder(false)
        console.log('Error on create cart: ', error)
      }
    }
  }

  async function handleMoveBooking(targetEvent: EventType) {
    if (isRequestingChangeBooking) {
      return false
    }

    setIsRequestingChangeBooking(true)
    const currentAmeliaBookingData = windowCurrentAmeliaBookingData;
    const getEventTargetUrl = `${window.wpAjaxUrl}?action=wpamelia_api&call=/events/${targetEvent.id}&ameliaNonce=${windowAjaxNonce}`;
    const createBookingUrl = `${window.wpAjaxUrl}?action=wpamelia_api&call=/bookings&ameliaNonce=${windowAjaxNonce}`;
    const deleteBookingUrl = `${window.wpAjaxUrl}?action=wpamelia_api&call=/events/bookings/delete/${windowBookingId}&ameliaNonce=${windowAjaxNonce}`;
    const wpAjaxUrl = window.wpAjaxUrl;
    // Get target event data.
    const resultEventData = await axios.get(getEventTargetUrl)
    if (resultEventData?.data?.data?.event) {
      // Matching for update eventTicketId from target event.
      const eventTicketIdsMatching: number[] = [];
      const event: EventType = resultEventData?.data?.data?.event;
      event?.customTickets.forEach(ticket => {
        const targetTicket = currentAmeliaBookingData.bookings[0].ticketsData.find((currentTicket: CustomTicketType) => {
          const currentPrice: number = parseInt(`${currentTicket.price}`, 10)
          return currentTicket.name.toLowerCase() === ticket.name.toLowerCase() && currentPrice === ticket.price
        })
        if (targetTicket) {
          targetTicket.id = 0;
          targetTicket.eventTicketId = ticket.id;
          eventTicketIdsMatching.push(ticket.id);
        }
      })

      // Fiiter out ticketsData not included in new booking
      currentAmeliaBookingData.bookings[0].ticketsData = currentAmeliaBookingData.bookings[0].ticketsData.filter((ticket: AmeliaTicketType) => eventTicketIdsMatching.indexOf(ticket.eventTicketId) !== -1)

      // Updated ameliabooking data for make new payload for create new booking
      currentAmeliaBookingData.name = event.name;
      currentAmeliaBookingData.eventId = event.id;
      currentAmeliaBookingData.dateTimeValues = [];
      event?.periods.forEach(period => {
        currentAmeliaBookingData.dateTimeValues.push({
          start: period.periodStart,
          end: period.periodEnd
        })
      })
    } else {
      console.log('Can not find the target event data');
      alert(t('error_can_not_find_target_event_data_please_try_again'))
      setIsRequestingChangeBooking(false)
      return false;
    }

    try {
      // Create new booking. ( Move Booking to new event )
      const payloadNewBooking = currentAmeliaBookingData;
      await axios.post(createBookingUrl, payloadNewBooking);
      // // Delete current booking.
      await axios.post(deleteBookingUrl);

      const ticketsData = payloadNewBooking.bookings[0].ticketsData.map((_ticket: any) => ({
        eventTicketId: _ticket.eventTicketId,
        persons: _ticket.persons,
        name: _ticket.name,
        price: _ticket.price
      }))

      await window.jQuery.post(wpAjaxUrl, {
        action: 'updateOrderAmeliaBookingData',
        orderId: windowOrderId,
        name: payloadNewBooking.name,
        eventId: payloadNewBooking.eventId,
        dateTimeValues: payloadNewBooking.dateTimeValues,
        ticketsData,
        nonce: windowAjaxNonceEditBooking,
      })

      setIsRequestingChangeBooking(false)
      window.location.href = document.referrer
    } catch (error) {
      alert(t('error_something_went_wrong'))
      setIsRequestingChangeBooking(false)
    }
  }
}

export default App;

const Wrapper = styled.div`
  color: white;
  display: flex;
`

const BoxLeft = styled.div`
  min-width: 400px;

  @media (max-width: 1024px) {
    min-width: 300px;
  }

  @media (max-width: 991px) {
    min-width: 260px;
  }

  @media (max-width: 767px) {
    display: none;
  }
`

const BoxRight = styled.div`
  flex: 1;
`

const ListSteps = styled.ul`
  list-style: none;
  padding-left: 0;
`

const StepDetailsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 33px;

  @media (max-width: 767px) {
    margin-bottom: 24px;
  }
`

const StepName = styled.h3`
  text-transform: uppercase;

  @media (max-width: 767px) {
    line-height: 1.8em;
  }
`

const CircleStepWrapper = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
  }
`

const NextStepDetail = styled.span`
  font-size: 12px;
  display: none;

  @media (max-width: 767px) {
    display: block;
  }
`

const SelectWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  margin-bottom: 15px;

  @media (max-width: 767px) {
    width: 100%;
  }

  > .ant-select  {
   widtH: 100%;
  }
`

const OptionCountry = styled.div`
  display: inline-flex;
  align-items: center;
  column-gap: 5px;
  color: black;
  height: 100%;
`